import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { MembershipCertificate } from '../../models/membership-certificate';
import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { Certificate } from '../../models/certificate';
import { CertificateService } from '../../services/certificate.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { UtilService } from 'src/app/services/util-service/utilService';
import { TemplateNode } from 'src/app/components/product-certification/certifications/certifications/certification-template-editor/certification-template-editor.component';
import { CertificationVariantVersionService } from 'src/app/services/dataServices/cetrification-variant-version-service/certification-variant-version-service';
import { DialogService } from 'src/app/services/dialog-service/dialog-service.service';
import { UserService } from 'src/app/services/user-service/userservice';
import { CertDetailsComponent } from './cert-details/cert-details.component';
import {
  TableColumn,
  TableEmptyState,
  TableInspector,
  TablePageComponent,
  TableRowAction,
  TableRowActionEvent,
  TableSummaryItem,
  TableTone
} from 'src/app/con-x/table/table-page.component';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-certificates-component',
  templateUrl: './certificates.component.html',
  styleUrls: ['./certificates.component.scss'],
  imports: [
    CertDetailsComponent,
    TablePageComponent,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ]
})
export class CertificatesComponent implements OnInit, OnDestroy {

  @ViewChild('vcDialog', { static: true }) vcDialog: TemplateRef<unknown>;

  @Input() isAuth: boolean = false;

  /**
   * Supplies the organisation context when the wallet is embedded outside an
   * organisation route, for example in the marketplace detail dialog.
   */
  @Input()
  set walletOwnerId(value: number | null | undefined) {
    const ownerId = Number(value);
    this.explicitOwnerId = Number.isFinite(ownerId) && ownerId > 0 ? ownerId : null;
    if (this.explicitOwnerId !== null) {
      this.setOwnerId(this.explicitOwnerId);
    }
  }

  private ownerId: number | null = null;
  private explicitOwnerId: number | null = null;
  certificates: Certificate[] = [];

  showInOrgCard: Map<number, boolean> = new Map();

  // NEW: view mode per certificate: 'tree' | 'raw'
  viewMode: Map<number, 'tree' | 'raw'> = new Map();

  // NEW: parsed VC as TemplateNode[]
  vcTreeData: Map<number, TemplateNode[]> = new Map();

  loading: boolean = false;
  errorMessage = '';
  membershipError = '';
  private readonly destroyed = new Subject<void>();
  private loadSubscription?: Subscription;
  selectedCertificate: Certificate | null = null;
  vcCertificate: Certificate | null = null;
  private vcDialogRef: MatDialogRef<unknown> | null = null;

  statusMapping = {
    'IN_BEARBEITUNG': 'In Bearbeitung',
    'AKTIV': 'Aktiv',
    'ABGELAUFEN': 'Abgelaufen',
    'ZURUECKGEZOGEN': 'Zurückgezogen',
    'ANGEFRAGT': 'Angefragt',
    'ABGELEHNT': 'Abgelehnt'
  };

  constructor(
    private router: Router,
    private activeRouter: ActivatedRoute,
    private certificateService: CertificateService,
    private cdr: ChangeDetectorRef,
    private utilService: UtilService,
    private certificationVariantVersionService: CertificationVariantVersionService,
    public dialogService: DialogService,
    private userService: UserService,
    private matDialog: MatDialog
  ) { }

  get columns(): readonly TableColumn<Certificate>[] {
    const columns: TableColumn<Certificate>[] = [
      { key: 'name', label: 'Name', value: row => row.getName() },
      { key: 'issuer', label: 'Aussteller', value: row => row.getIssuer() },
      { key: 'createdAt', label: 'Erstellt', value: row => row.getCreatedAt(), type: 'datetime' },
      { key: 'updatedAt', label: 'Geändert', value: row => row.getUpdatedAt(), type: 'datetime' },
      { key: 'validUntil', label: 'Gültig bis', value: row => row.getValidUntil(), type: 'date', emptyText: '—' },
      {
        key: 'status',
        label: 'Status',
        value: row => this.statusLabel(row),
        type: 'badge',
        tone: row => this.statusTone(row)
      }
    ];
    if (this.isAuth) {
      columns.push({
        key: 'showInOrgCard',
        label: 'Kachel-Anzeige',
        value: row => row instanceof MembershipCertificate ? null : row.getShowInOrgCard(),
        type: 'boolean',
        trueLabel: 'Ja',
        falseLabel: 'Nein'
      });
    }
    return columns;
  }

  get rowActions(): readonly TableRowAction<Certificate>[] {
    return [
      { id: 'open-onboarding', label: 'Onboarding anzeigen', icon: 'how_to_reg', hidden: row => !(row instanceof MembershipCertificate) },
      { id: 'show-vc', label: 'VC-Inhalt anzeigen', icon: 'data_object', hidden: row => row instanceof MembershipCertificate && !row.credential },
      { id: 'open-certificate', label: 'Zertifikat im Marktplatz', icon: 'open_in_new', hidden: row => row instanceof MembershipCertificate },
      { id: 'open-certifier', label: 'Zertifizierer im Marktplatz', icon: 'business', hidden: row => row instanceof MembershipCertificate },
      {
        id: 'confirm', label: 'Zertifikat bestätigen', icon: 'check_circle',
        hidden: row => row instanceof MembershipCertificate || !this.isAuth || !this.isOrgAdmin() || row.getStatus() !== 'IN_BEARBEITUNG'
      },
      {
        id: 'reject', label: 'Zertifikat ablehnen', icon: 'cancel', tone: 'warn',
        hidden: row => row instanceof MembershipCertificate || !this.isAuth || !this.isOrgAdmin() || row.getStatus() !== 'IN_BEARBEITUNG'
      },
      {
        id: 'delete', label: 'Zertifikat löschen', icon: 'delete', tone: 'warn',
        hidden: row => row instanceof MembershipCertificate || !this.isAuth || !this.isOrgAdmin() || row.getStatus() !== 'AKTIV'
      },
      {
        id: 'toggle-card', label: 'Kachel-Anzeige wechseln', icon: 'dashboard_customize',
        hidden: row => row instanceof MembershipCertificate || !this.isAuth
      }
    ];
  }

  get inspector(): TableInspector<Certificate> {
    return {
      ariaLabel: 'Details des ausgewählten Zertifikats',
      title: row => row.getName(),
      subtitle: row => this.statusLabel(row),
      description: row => row instanceof MembershipCertificate ? this.membershipDescription(row) : `Ausgestellt von ${row.getIssuer()}`,
      icon: 'verified',
      tone: row => this.statusTone(row),
      fields: [
        { label: 'Erstellt', value: row => row.getCreatedAt(), type: 'datetime' },
        { label: 'Geändert', value: row => row.getUpdatedAt(), type: 'datetime' },
        { label: 'Gültig bis', value: row => row.getValidUntil(), type: 'date', emptyText: '—' },
        { label: 'Kachel-Anzeige', value: row => row instanceof MembershipCertificate ? null : row.getShowInOrgCard(), type: 'boolean', trueLabel: 'Ja', falseLabel: 'Nein' }
      ],
      actions: this.rowActions
    };
  }

  get summaryItems(): readonly TableSummaryItem<Certificate>[] {
    return [
      { label: 'Einträge', value: this.certificates.length, icon: 'verified' },
      { label: 'Aktiv', value: this.certificates.filter(row => row.getStatus() === 'AKTIV').length, icon: 'check_circle' }
    ];
  }

  readonly emptyState: TableEmptyState = {
    icon: 'verified_user',
    title: 'Keine Wallet-Einträge vorhanden',
    description: 'Für diese Organisation sind noch keine Zertifizierungen oder bestätigten Mitgliedschaften vorhanden.'
  };

  ngOnInit(): void {
    this.activeRouter.parent.params.pipe(takeUntil(this.destroyed)).subscribe((params: Params) => {
      let routeOwnerId = Number(params['organisationId']);
      if (this.explicitOwnerId === null && routeOwnerId) {
        this.setOwnerId(routeOwnerId);
      }
    });

    this.activeRouter.queryParams.pipe(takeUntil(this.destroyed)).subscribe((queryParams: Params) => {
      let orgId = queryParams['orgId'];
      let queryOwnerId = orgId ? parseInt(orgId, 10) : null;
      if (this.explicitOwnerId === null && queryOwnerId && !isNaN(queryOwnerId)) {
        this.setOwnerId(queryOwnerId);
      }
    });
  }

  private setOwnerId(ownerId: number): void {
    if (ownerId !== this.ownerId) {
      this.ownerId = ownerId;
      this.loadCert();
    }
  }

  loadCert(): void {
    if (!this.ownerId) return;

    this.loadSubscription?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';
    this.membershipError = '';
    this.selectedCertificate = null;
    this.loadSubscription = forkJoin({
      certificates: this.certificateService.getCertificates(this.ownerId),
      membership: this.isAuth ? this.certificateService.getMembership(this.ownerId).pipe(
        catchError(() => {
          this.membershipError = 'Die Mitgliedschaft konnte nicht geladen werden. Bitte aktualisieren Sie das Wallet.';
          return of(null);
        })
      ) : of(null)
    }).pipe(takeUntil(this.destroyed)).subscribe(({ certificates: res, membership }) => {
      this.loading = false;
      this.certificates = [];
      this.viewMode.clear();
      this.vcTreeData.clear();
      if (res.message === "success") {
        this.certificates = res.value;
        this.showInOrgCard.clear();
        this.certificates.forEach((certificate) => {
          const id = certificate.getId();
          this.showInOrgCard.set(id, certificate.getShowInOrgCard());
          const raw = certificate.getRawJson();
          if (raw && raw.vcJwt) {
            const decoded = this.utilService.decodeValue(raw.vcJwt);
            certificate.setRawJson(decoded); // keep your current behavior

            this.viewMode.set(id, 'tree');
            this.vcTreeData.set(id, this.buildTreeFromVC(decoded, certificate));
          }
        });
      }
      else this.errorMessage = 'Diese Zertifikate konnten nicht geladen werden, bitte versuchen Sie es erneut.';
      if (membership?.status?.status === 'accepted') {
        let vc = membership.credential?.verifiableCredential;
        vc = vc?.credential ?? vc;
        if (typeof vc === 'string') {
          try { vc = this.utilService.decodeValue(vc)?.vc; } catch { vc = null; }
        }
        const row = new MembershipCertificate(membership.status, membership.credential, vc);
        this.certificates = [row, ...this.certificates];
        if (membership.credential) {
          this.viewMode.set(row.getId(), 'raw');
          this.vcTreeData.set(row.getId(), this.buildTreeFromVC(vc, row));
        }
        if (this.errorMessage) {
          this.membershipError = this.errorMessage;
          this.errorMessage = '';
        }
      }
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
    this.vcDialogRef?.close();
  }

  private membershipDescription(row: MembershipCertificate): string {
    if (row.credential) return 'Mitgliedsausweis aus dem verknüpften Wallet abgerufen.';
    return row.membership.credentialStatus === 'RETRY'
      ? 'Teilnahme bestätigt. Der Mitgliedsausweis konnte noch nicht abgerufen werden. Bitte aktualisieren Sie das Wallet.'
      : 'Teilnahme bestätigt. Der Mitgliedsausweis ist noch nicht verfügbar.';
  }

  handleRowAction(event: TableRowActionEvent<Certificate>): void {
    if (event.row instanceof MembershipCertificate && !['show-vc', 'open-onboarding'].includes(event.actionId)) return;
    if (event.actionId === 'open-onboarding') void this.router.navigate(['/organisations', this.ownerId, 'data-space-onboarding']);
    if (event.actionId === 'show-vc') this.openVCContent(event.row);
    if (event.actionId === 'open-certificate') this.navigateToCertification(event.row.getCertId());
    if (event.actionId === 'open-certifier') this.navigateToCertifier(event.row.getIssuerId());
    if (event.actionId === 'confirm') this.confirmCertification(event.row);
    if (event.actionId === 'reject') this.revokeCert(event.row);
    if (event.actionId === 'delete') this.showDeleteCertificatePopup(event.row.getId());
    if (event.actionId === 'toggle-card') this.toggleShowInOrgCard(event.row.getId());
  }

  selectCertificate(certificate: Certificate | null): void {
    this.selectedCertificate = certificate;
  }

  private openVCContent(certificate: Certificate): void {
    this.vcDialogRef?.close();
    this.vcCertificate = certificate;
    this.vcDialogRef = this.matDialog.open(this.vcDialog, {
      width: '64rem',
      maxWidth: '92vw',
      maxHeight: '90vh',
      autoFocus: 'dialog',
      restoreFocus: true
    });
    this.vcDialogRef.afterClosed().subscribe(() => {
      this.vcCertificate = null;
      this.vcDialogRef = null;
    });
  }

  certificateId = (certificate: Certificate): string => certificate instanceof MembershipCertificate
    ? `membership:${certificate.membership.id}` : `certificate:${certificate.getId()}`;

  private statusLabel(certificate: Certificate): string {
    if (certificate instanceof MembershipCertificate) return certificate.credential ? 'Gespeichert' : 'Bestätigt';
    const status = certificate.getStatus();
    return this.statusMapping[status] || status.toLowerCase().replace(/_/g, ' ').replace(/^./, value => value.toUpperCase());
  }

  private statusTone(certificate: Certificate): TableTone {
    if (certificate instanceof MembershipCertificate) return certificate.credential ? 'success' : 'accent';
    if (certificate.getStatus() === 'AKTIV') return 'success';
    if (certificate.getStatus() === 'ZURUECKGEZOGEN' || certificate.getStatus() === 'ABGELEHNT') return 'warn';
    if (certificate.getStatus() === 'IN_BEARBEITUNG' || certificate.getStatus() === 'ANGEFRAGT') return 'accent';
    return 'neutral';
  }

  // NEW: simple JSON → TemplateNode[] converter tailored to VC structure
  private buildTreeFromVC(vc: any, certificate: Certificate): TemplateNode[] {
    if (!vc || typeof vc !== 'object') return [];

    const root: TemplateNode = {
      name: 'Verifiable Credential',
      type: 'object',
      expanded: true,
      children: []
    };

    const addChild = (parent: TemplateNode, name: string, value: any) => {
      const node: TemplateNode = {
        name,
        type: this.detectType(value),
        expanded: false
      };

      if (Array.isArray(value)) {
        node.expanded = false;
        node.children = value.map((item, index) => {
          const itemNode: TemplateNode = {
            name: `${name}[${index}]`,
            type: this.detectType(item),
            expanded: false
          };
          if (item && typeof item === 'object') {
            itemNode.children = this.objectToNodes(item);
          } else {
            itemNode.value = item;
          }
          return itemNode;
        });
      } else if (value && typeof value === 'object') {
        node.children = this.objectToNodes(value);
      } else {
        node.value = value;
      }

      if (!parent.children) parent.children = [];
      parent.children.push(node);
    };

    // map your VC fields explicitly to readable nodes
    if (vc['@context']) {
      addChild(root, '@context', vc['@context']);
    }
    if (vc.type) {
      addChild(root, 'type', vc.type);
    }
    if (vc.id) {
      addChild(root, 'id', vc.id);
    }
    if (vc.issuer) {
      addChild(root, 'issuer', vc.issuer);
    }
    if (vc.validFrom) {
      addChild(root, 'validFrom', vc.validFrom);
    }
    if (vc.validUntil) {
      addChild(root, 'validUntil', vc.validUntil);
      certificate.setValidUntil(vc.validUntil);
    }
    if (vc.credentialSubject) {
      const csNode: TemplateNode = {
        name: 'credentialSubject',
        type: 'object',
        expanded: true,
        children: this.objectToNodes(vc.credentialSubject)
      };
      if (!root.children) root.children = [];
      root.children.push(csNode);
    }

    return [root];
  }

  private objectToNodes(obj: any): TemplateNode[] {
    return Object.keys(obj).map(key => {
      const value = obj[key];
      const node: TemplateNode = {
        name: key,
        type: this.detectType(value),
        expanded: false
      };

      if (Array.isArray(value)) {
        node.children = value.map((item, index) => {
          const itemNode: TemplateNode = {
            name: `${key}[${index}]`,
            type: this.detectType(item),
            expanded: false
          };
          if (item && typeof item === 'object') {
            itemNode.children = this.objectToNodes(item);
          } else {
            itemNode.value = item;
          }
          return itemNode;
        });
      } else if (value && typeof value === 'object') {
        node.children = this.objectToNodes(value);
      } else {
        node.value = value;
      }

      return node;
    });
  }

  private detectType(value: any): 'string' | 'number' | 'boolean' | 'object' | 'array' {
    if (Array.isArray(value)) return 'array';
    const t = typeof value;
    if (t === 'number') return 'number';
    if (t === 'boolean') return 'boolean';
    if (t === 'object') return 'object';
    return 'string';
  }

  isOrgAdmin(): boolean {
    if (!this.ownerId) return false;
    // Ensure ownerId is compared as number if membership organisationId is number
    const targetOrgId = Number(this.ownerId);
    const membership = this.userService.currentMemberships.find(m => m.organisationId === targetOrgId);
    return membership?.role === 'ADMIN';
  }

  confirmCertification(certificate: Certificate): void {
    this.certificationVariantVersionService.confirmCertification(certificate.getId()).subscribe({
      next: () => {
        this.dialogService.openDialog('Erfolgreich', 'Zertifikat erfolgreich bestätigt.');
        this.loadCert();
      },
      error: (err) => {
        this.dialogService.openDialog('Fehler', 'Bestätigung fehlgeschlagen: ' + err.message);
      }
    });
  }

  revokeCert(certificate: Certificate): void {
    const membership = this.userService.currentMemberships.find(m => m.organisationId === Number(this.ownerId));
    const holderName = membership ? membership.name : 'Ihre Organisation';

    const dialogRef = this.dialogService.openRevokeCertificationDialog({
      certName: certificate.getName(),
      holderName: holderName,
      issuanceDate: certificate.getCreatedAt()
    });

    dialogRef.afterClosed().subscribe(reason => {
      if (reason) {
        this.executeRevocation(certificate, reason, false);
      }
    });

  }

  private executeRevocation(certificate: Certificate, reason: string, isReject: boolean): void {
    const loadingDialog = this.dialogService.openFileUploadLoadingDialog(
      isReject ? 'Zertifizierung wird abgelehnt...' : 'Zertifikat wird zurückgezogen...'
    );

    this.certificationVariantVersionService.revokeCertification(certificate.getId(), reason).subscribe({
      next: () => {
        loadingDialog.close();
        const msg = isReject ? 'Zertifizierung wurde abgelehnt.' : 'Zertifizierung wurde zurückgezogen.';
        this.dialogService.openDialog('Erfolgreich', msg);
        this.loadCert();
      },
      error: (err) => {
        loadingDialog.close();
        this.dialogService.openDialog('Fehler', 'Aktion fehlgeschlagen: ' + (err.message || err));
      }
    });
  }

  navigateToCertification(certId: number) {
    this.router.navigate(['/marktplatz'], {
      queryParams: {
        tab: 1,
        certId: certId,
        certificationId: certId
      },
      queryParamsHandling: ''
    });
  }

  navigateToCertifier(orgId: number) {
    this.router.navigate(['/marktplatz'], {
      queryParams: {
        tab: 2,
        orgId: orgId,
        organisationId: orgId
      },
      queryParamsHandling: ''
    });
  }

  showRevocationReason(certificate: Certificate): void {
    console.log(certificate.getRevocationReason());
    this.dialogService.openWiderInfoDialog('Grund für das Zurückziehen', certificate.getRevocationReason());
  }

  toggleShowInOrgCard(certificateId: number): void {
    this.loading = true;
    if (this.certificates.filter(certificate => certificate.getShowInOrgCard()).length >= 4) {
      this.loading = false;
      this.dialogService.openDialog(
        "Zertifikat anzeigen",
        "Sie können nur bis zu 4 Zertifikate auf Ihrem Organisationskachel im Marktplatz anzeigen."
      );
      return;
    }
    this.showInOrgCard.set(certificateId, !this.showInOrgCard.get(certificateId));
    this.certificates.find((certificate) => certificate.getId() === certificateId)
      ?.setShowInOrgCard(this.showInOrgCard.get(certificateId));
    this.certificateService.showInOrgCard(certificateId, this.showInOrgCard.get(certificateId)).subscribe(res => {
      this.loading = false;
      if (res.message === "success") {
        let message = this.showInOrgCard.get(certificateId) ?
          "Das Zertifikat wird auf Ihrem Organisationskachel im Marktplatz angezeigt." :
          "Das Zertifikat wird nicht auf Ihrem Organisationskachel im Marktplatz angezeigt.";
        this.dialogService.openDialog("Zertifikat anzeigen", message);
      } else {
        this.dialogService.openDialog(
          "Zertifikat anzeigen",
          "Die Einstellung konnte nicht gespeichert werden, bitte versuchen Sie es erneut."
        );
      }
      this.cdr.detectChanges();
    });
  }

  showDeleteCertificatePopup(certificateId: number): void {
    this.dialogService.openConfirmDialog("Zertifikat löschen", "Nein", "Ja", "Sind Sie sicher, dass Sie das Zertifikat löschen möchten?").subscribe(res => {
      if (res) {
        this.deleteCertificate(certificateId);
      }
    });
  }

  deleteCertificate(certificateId: number): void {
    this.loading = true;
    this.certificateService.deleteCertificate(certificateId).subscribe(res => {
      this.loading = false;
      if (res.message === "success") {
        this.certificates = this.certificates.filter(certificate => certificate.getId() !== certificateId);
        this.dialogService.openDialog("Zertifikat löschen", "Das Zertifikat wurde erfolgreich gelöscht.");
      } else {
        this.dialogService.openDialog("Zertifikat löschen", "Das Zertifikat konnte nicht gelöscht werden, bitte versuchen Sie es erneut.");
      }
      this.cdr.detectChanges();
    });
  }

}
